import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer,
} from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import {
  CORE_PALETTE, AXIS_TICK_STYLE, CHART_MARGIN, formatYAxisLabel, renderMonthYearTick, MONTH_YEAR_AXIS_HEIGHT,
} from "@/analytics/chart.utilities";
import type { ChartItem } from "@/components/SimpleChartGrid";
import { displayAgeBandLabel } from "@/lib/ageBandLabels";
import {
  contextFromRows, contextFromWideRows, contextFromGrouped,
} from "@/analytics/charts/chart.context";

export const DEBTORS_AGE_BANDS = ["0-30", "31-60", "61-90", "91-120", "120-180", "180+"];

export const DEBTORS_CHART_KEYS = [
  "monthly", "byAge", "risk", "byOffice", "bySL", "byLoc", "topMgrs", "avgAge",
] as const;
export type DebtorsChartKey = typeof DEBTORS_CHART_KEYS[number];

export interface DebtorsChartData {
  monthly?:  { monthLabel: string; total_aged_debt_gbp: number }[];
  byAge?:    Record<string, number | string>[];
  risk?:     { monthLabel: string; risk_pct: number }[];
  byOffice?: Record<string, number | string>[];
  bySL?:     { data: Record<string, number | string>[]; series: string[] };
  byLoc?:    { data: Record<string, number | string>[]; series: string[] };
  topMgrs?:  { manager: string; aged_debt_gbp: number }[];
  avgAge?:   { monthLabel: string; avg_age_days: number }[];
}

export interface BuildDebtorsChartsArgs {
  data: DebtorsChartData;
  errors: Record<string, boolean>;
  loadingKeys: Set<string>;
  retryChart: (key: DebtorsChartKey) => void;
  palette: string[];
}

export function buildDebtorsChartItems({
  data, errors, loadingKeys, retryChart, palette,
}: BuildDebtorsChartsArgs): ChartItem[] {
  return [
    {
      id: "monthly",
      title: "Total Aged Debt £ Monthly",
      description: "Total outstanding aged debt in GBP across all clients, tracked month by month.",
      isLoading: loadingKeys.has("monthly"),
      hasError: errors.monthly,
      onRetry: () => retryChart("monthly"),
      getContextData: () => contextFromRows(data.monthly, "monthLabel", { total_aged_debt_gbp: "Aged debt £" }, "gbp"),
      render: (h) => (
        <ChartContainer config={{}} className={`${h} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.monthly ?? []} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="monthLabel" tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
              <YAxis width={55} tickFormatter={v => formatYAxisLabel(v, "gbp")} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar
                dataKey="total_aged_debt_gbp"
                name="Aged Debt £"
                fill={CORE_PALETTE.forestGreen}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      ),
    },
    {
      id: "byAge",
      title: "Aged Debt £ by Age Band (Stacked)",
      description: "Monthly aged debt split by how long each balance has been outstanding, from 0-30 days to 180+.",
      isLoading: loadingKeys.has("byAge"),
      hasError: errors.byAge,
      onRetry: () => retryChart("byAge"),
      getContextData: () => contextFromWideRows(data.byAge, "monthLabel", "gbp"),
      render: (h) => (
        <ChartContainer config={{}} className={`${h} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.byAge ?? []} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="monthLabel" tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
              <YAxis width={55} tickFormatter={v => formatYAxisLabel(v, "gbp")} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Legend />
              {DEBTORS_AGE_BANDS.map((b, i) => (
                <Bar key={b} dataKey={b} name={displayAgeBandLabel(b)} stackId="age" fill={palette[i % palette.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      ),
    },
    {
      id: "risk",
      title: "Risk Concentration (>90d % of total)",
      description: "Share of total aged debt outstanding for more than 90 days — a proxy for collection risk.",
      isLoading: loadingKeys.has("risk"),
      hasError: errors.risk,
      onRetry: () => retryChart("risk"),
      getContextData: () => contextFromRows(data.risk, "monthLabel", { risk_pct: "Over 90 days %" }, "percent"),
      render: (h) => (
        <ChartContainer config={{}} className={`${h} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.risk ?? []} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="monthLabel" tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
              <YAxis width={45} domain={[0, 100]} tickFormatter={v => formatYAxisLabel(v, "percent")} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line type="monotone" dataKey="risk_pct" name="Risk %" stroke={CORE_PALETTE.red} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      ),
    },
    {
      id: "byOffice",
      title: "Aged Debt £ by Location & Age Band (latest month)",
      description: "Latest month's aged debt by office location, split into age bands.",
      isLoading: loadingKeys.has("byOffice"),
      hasError: errors.byOffice,
      onRetry: () => retryChart("byOffice"),
      getContextData: () => contextFromWideRows(data.byOffice, "office", "gbp"),
      render: (h) => (
        <ChartContainer config={{}} className={`${h} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.byOffice ?? []} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="office" tick={AXIS_TICK_STYLE} />
              <YAxis width={55} tickFormatter={v => formatYAxisLabel(v, "gbp")} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Legend />
              {DEBTORS_AGE_BANDS.map((b, i) => (
                <Bar key={b} dataKey={b} name={displayAgeBandLabel(b)} stackId="age" fill={palette[i % palette.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      ),
    },
    {
      id: "bySL",
      title: "Aged Debt £ by Service Line over time",
      description: "Monthly aged debt trend, one line per service line.",
      isLoading: loadingKeys.has("bySL"),
      hasError: errors.bySL,
      onRetry: () => retryChart("bySL"),
      getContextData: () => contextFromGrouped(data.bySL, "monthLabel", "gbp"),
      render: (h) => {
        const series: string[] = data.bySL?.series ?? [];
        const rows = data.bySL?.data ?? [];
        return (
          <ChartContainer config={{}} className={`${h} w-full`}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="monthLabel" tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
                <YAxis width={55} tickFormatter={v => formatYAxisLabel(v, "gbp")} tick={AXIS_TICK_STYLE} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Legend />
                {series.map((s, i) => (
                  <Line key={s} type="monotone" dataKey={s} name={s} stroke={palette[i % palette.length]} strokeWidth={2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        );
      },
    },
    {
      id: "byLoc",
      title: "Aged Debt £ by Location over time",
      description: "Monthly aged debt trend, one line per office location.",
      isLoading: loadingKeys.has("byLoc"),
      hasError: errors.byLoc,
      onRetry: () => retryChart("byLoc"),
      getContextData: () => contextFromGrouped(data.byLoc, "monthLabel", "gbp"),
      render: (h) => {
        const series: string[] = data.byLoc?.series ?? [];
        const rows = data.byLoc?.data ?? [];
        return (
          <ChartContainer config={{}} className={`${h} w-full`}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="monthLabel" tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
                <YAxis width={55} tickFormatter={v => formatYAxisLabel(v, "gbp")} tick={AXIS_TICK_STYLE} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Legend />
                {series.map((s, i) => (
                  <Line key={s} type="monotone" dataKey={s} name={s} stroke={palette[i % palette.length]} strokeWidth={2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        );
      },
    },
    {
      id: "topMgrs",
      title: "Top Managers by Aged Debt £ (latest month)",
      description: "Managers carrying the highest aged debt balances in the latest month.",
      isLoading: loadingKeys.has("topMgrs"),
      hasError: errors.topMgrs,
      onRetry: () => retryChart("topMgrs"),
      getContextData: () => contextFromRows(data.topMgrs, "manager", { aged_debt_gbp: "Aged debt £" }, "gbp"),
      render: (h) => (
        <ChartContainer config={{}} className={`${h} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.topMgrs ?? []} layout="vertical" margin={{ ...CHART_MARGIN, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={v => formatYAxisLabel(v, "gbp")} tick={AXIS_TICK_STYLE} />
              <YAxis type="category" dataKey="manager" tick={AXIS_TICK_STYLE} width={150} interval={0} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="aged_debt_gbp" name="Aged Debt £" fill={CORE_PALETTE.purple} />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      ),
    },
    {
      id: "avgAge",
      title: "Average Debt Age (days) Monthly",
      description: "Average number of days debt has been outstanding, tracked month by month.",
      isLoading: loadingKeys.has("avgAge"),
      hasError: errors.avgAge,
      onRetry: () => retryChart("avgAge"),
      getContextData: () => contextFromRows(data.avgAge, "monthLabel", { avg_age_days: "Average age (days)" }, "days"),
      render: (h) => (
        <ChartContainer config={{}} className={`${h} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.avgAge ?? []} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="monthLabel" tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
              <YAxis width={45} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line type="monotone" dataKey="avg_age_days" name="Avg Age (days)" stroke={CORE_PALETTE.actionOrange} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      ),
    },
  ];
}
